import { Reservations } from '@/global';
import {
  calculateBalance,
  calculateDeposit,
  calculateTotal,
} from '@/lib/reservation';
import { Button } from '@/ui/button';
import { ReceiptIcon } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReceiptEncoder from '@point-of-sale/receipt-printer-encoder';

// Add type definitions for Web Bluetooth API
declare global {
  interface Navigator {
    bluetooth: {
      requestDevice(options: {
        filters: Array<{ services: string[] }>;
      }): Promise<BluetoothDevice>;
    };
  }
}

interface BluetoothDevice {
  gatt: {
    connect(): Promise<BluetoothRemoteGATTServer>;
  };
}

interface BluetoothRemoteGATTServer {
  getPrimaryService(service: string): Promise<BluetoothRemoteGATTService>;
}

interface BluetoothRemoteGATTService {
  getCharacteristic(
    characteristic: string
  ): Promise<BluetoothRemoteGATTCharacteristic>;
}

interface BluetoothRemoteGATTCharacteristic {
  writeValue(data: BufferSource): Promise<void>;
}

const ThermalTicket = ({ reservation }: { reservation: Reservations }) => {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [printCharacteristic, setPrintCharacteristic] =
    useState<BluetoothRemoteGATTCharacteristic | null>(null);
  const [progressVisible, setProgressVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.crossOrigin = 'anonymous';
    }
  }, []);

  const handlePrint = async () => {
    try {
      let characteristic = printCharacteristic;
      if (!characteristic) {
        const device = await navigator.bluetooth.requestDevice({
          filters: [{ services: ['000018f0-0000-1000-8000-00805f9b34fb'] }],
        });
        const server = await device.gatt.connect();
        const service = await server.getPrimaryService(
          '000018f0-0000-1000-8000-00805f9b34fb'
        );
        characteristic = await service.getCharacteristic(
          '00002af1-0000-1000-8000-00805f9b34fb'
        );
        setPrintCharacteristic(characteristic);
      }

      const encoder = new ReceiptEncoder();

      // Boarding pass header
      encoder.invert(true);
      encoder.size(2, 2);
      encoder.text('BOARDING PASS');
      encoder.size(1, 1);
      encoder.invert(false);
      encoder.newline();
      encoder.newline();

      // Service name
      encoder.bold(true);
      const serviceName = reservation.service.name;
      encoder.text(serviceName);
      encoder.bold(false);
      encoder.newline();

      encoder.text(`${reservation.booked_date} - ${reservation.shift.name}`);
      encoder.newline();
      encoder.newline();
      encoder.rule({ style: 'double', width: 32 });
      encoder.table(
        [
          { width: 20, align: 'left' },
          { width: 3, align: 'left' },
          { width: 9, align: 'right' },
        ],
        reservation.variants.map((variant) => [
          variant.variant.name,
          variant.amount.toString(),
          variant.total.toString(),
        ])
      );
      encoder.rule({ style: 'single', width: 32 });
      encoder.text(`TOTALE: ${calculateTotal(reservation)}`);
      encoder.newline();
      encoder.text(`ACCONTO: ${calculateDeposit(reservation)}`);
      encoder.newline();
      encoder.text(`SALDO: ${calculateBalance(reservation)}`);
      encoder.newline();
      encoder.newline();
      // Print customer info
      encoder.text('CLIENTE');
      encoder.newline();
      encoder.text(`${reservation.customer_name}`);
      encoder.newline();
      encoder.text(`${reservation.customer_email}`);
      encoder.newline();
      encoder.text(`${reservation.customer_phone}`);
      encoder.newline();
      encoder.newline();

      encoder.qrcode(reservation.code.substring(0, 10), 1, 8);
      encoder.newline();
      encoder.barcode(reservation.code.substring(0, 10), 'code128');

      encoder.size(2, 2);
      encoder.invert(true);
      encoder.text(`${reservation.code.substring(0, 10)}`);
      encoder.invert(false);
      encoder.size(1, 1);

      encoder.newline();
      encoder.newline();

      // Cut paper
      encoder.cut();

      // Get the encoded data and send it to the printer
      const encodedData = encoder.encode();
      await characteristic.writeValue(encodedData);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to print.');
    } finally {
      setProgressVisible(false);
    }
  };

  return (
    <div>
      <img
        id="image"
        ref={imageRef}
        src="https://core.baetorra.com/assets/f094d4a4-e03a-4a40-826b-08f263e71523"
        alt="Printable"
        className="hidden"
      />

      <Button
        variant="outline"
        size="lg"
        className="w-full"
        onClick={handlePrint}
      >
        <ReceiptIcon className="mr-2 size-4" />
        {t('reservation.print-ticket-thermal')}
      </Button>

      {progressVisible && (
        <div className="mt-2 text-gray-600">Sending data to printer...</div>
      )}
      {error && <div className="mt-2 text-red-600">{error}</div>}
    </div>
  );
};

export default ThermalTicket;
